import { useState, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Container, Title, Banner, StyledSpinner } from '@tryhackme/thm-ui-components';
import { ReactComponent as CorporateImage } from 'src/images/svgs/corporate.svg';
import { Subscribers } from './subscribers';
import { Instructors } from './instructors';
import { SidePanel } from './components/sidepanel';
import { CorpColumns, CorpContent, CorpMainPage } from './corporate.styles';
import { useGetAllInstructorsOfACompanyQuery, useGetAllSubscribersOfACompanyQuery, useGetSingleCompanyQuery } from './corporate.slice';
import { CompanyItem, CompanySelectOption, CorporateContainerProps, CorporateParams } from './corporate.types';
import { CompanyBar } from './components';
import { COMPANY_ID_URL_QUERY_PARAM, defaultCompanyDetails, defaultUsersData } from './corporate.constants';
import { getInitialCompany } from './corporate.helpers';
const defaultTableParams = {
  page: 1,
  limit: 10,
  search: '',
  sort: '-email'
};
export const CorporateContainer = ({
  companiesData
}: CorporateContainerProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const urlCompanyId = searchParams.get(COMPANY_ID_URL_QUERY_PARAM);
  const initialCompany = useMemo(() => getInitialCompany(urlCompanyId, companiesData), [urlCompanyId, companiesData]);
  const [subscriberParams, setSubscriberParams] = useState<CorporateParams>({
    ...defaultTableParams,
    companyId: initialCompany.id
  });
  const [instructorParams, setInstructorParams] = useState<CorporateParams>({
    ...defaultTableParams,
    companyId: initialCompany.id
  });
  const {
    data: singleCompany,
    isLoading: companyLoading
  } = useGetSingleCompanyQuery({
    companyId: initialCompany.id
  });
  const {
    data: subscriberData,
    isLoading: subscriberLoading
  } = useGetAllSubscribersOfACompanyQuery(subscriberParams);
  const {
    data: instructorData,
    isLoading: instructorLoading
  } = useGetAllInstructorsOfACompanyQuery(instructorParams);
  const companyDetails: CompanyItem = singleCompany?.data?.companyDetails ?? defaultCompanyDetails;
  const handleCompanySelection = ({
    id
  }: CompanySelectOption) => {
    navigate(`?${COMPANY_ID_URL_QUERY_PARAM}=${id}`, {
      preventScrollReset: true
    });
    setSubscriberParams({
      ...defaultTableParams,
      companyId: id
    });
    setInstructorParams({
      ...defaultTableParams,
      companyId: id
    });
  };
  if (companyLoading || subscriberLoading || instructorLoading) {
    return <StyledSpinner color="black" />;
  }
  return <section data-sentry-component="CorporateContainer" data-sentry-source-file="corporate.tsx">
      <Banner Image={CorporateImage} data-sentry-element="Banner" data-sentry-source-file="corporate.tsx">
        <Container data-sentry-element="Container" data-sentry-source-file="corporate.tsx">
          <Title isInBanner titleText="Corporate" subTitleText="Manage Corporate clients and their users" data-sentry-element="Title" data-sentry-source-file="corporate.tsx" />
        </Container>
      </Banner>
      <CorpMainPage data-sentry-element="CorpMainPage" data-sentry-source-file="corporate.tsx">
        <CorpContent data-sentry-element="CorpContent" data-sentry-source-file="corporate.tsx">
          <SidePanel companyId={companyDetails?._id} companyLogo={companyDetails?.logo} companyName={companyDetails?.name} companyWebsite={companyDetails?.url} username={companyDetails?.name} expiryDate={companyDetails?.deadline} selfServeBilling={companyDetails?.subscription?.chargebee?.customerId} selfServeRenewal={companyDetails?.subscription?.chargebee?.cancellationScheduled ?? false} pathActive={companyDetails?.features?.paths ?? false} apiKeys={companyDetails?.apiInfo?.active ?? false} socSimEnabled={companyDetails?.features?.socSim ?? false} tokens={companyDetails?.tokens?.amount} ssoEnabled={companyDetails.features?.sso?.enabled ?? false} data-sentry-element="SidePanel" data-sentry-source-file="corporate.tsx" />
          <CorpColumns data-sentry-element="CorpColumns" data-sentry-source-file="corporate.tsx">
            <CompanyBar options={companiesData} selected={initialCompany} onChange={handleCompanySelection} data-sentry-element="CompanyBar" data-sentry-source-file="corporate.tsx" />
            <Subscribers companyDetails={companyDetails} subscriberData={subscriberData?.data ?? defaultUsersData} setFeedbackParams={setSubscriberParams} feedbackParams={subscriberParams} companyTrial={companyDetails?.isTrial ?? false} totalUsersCount={companyDetails.totalUsersCount ?? ''} data-sentry-element="Subscribers" data-sentry-source-file="corporate.tsx" />
            <Instructors companyDetails={companyDetails} instructorData={instructorData?.data ?? defaultUsersData} setFeedbackParams={setInstructorParams} feedbackParams={instructorParams} data-sentry-element="Instructors" data-sentry-source-file="corporate.tsx" />
          </CorpColumns>
        </CorpContent>
      </CorpMainPage>
    </section>;
};